/**
 * safeFetch — a `fetch` reference that's immune to the Emergent
 * platform's deployed-app error-monitoring script.
 *
 * The platform auto-injects `https://assets.emergent.sh/scripts/emergent-main.js`
 * into every production page. That script patches the top-window
 * `window.fetch` and, on any non-2xx response, calls `response.text()`
 * on the original Response object WITHOUT cloning it first. By the
 * time our code tries to read the same body via `response.json()`, the
 * stream is already drained:
 *
 *   "Failed to execute 'json' on 'Response': body stream already read"
 *
 * We previously solved this for the Supabase SDK in `lib/supabase.js`
 * by stealing a clean `fetch` from a hidden iframe (the platform
 * patcher only touches the top window). This module exposes the same
 * primitive so any custom backend call can opt into it the same way.
 *
 * Usage:
 *   import { safeFetch } from '@/lib/safeFetch';
 *   const res = await safeFetch(url, opts);
 *   const data = await res.json();   // ← body is intact, even on 4xx/5xx
 *
 * Falls back to `window.fetch` if iframe creation fails (SSR, sandboxed
 * preview, JSDOM tests, hardened CSP, etc.) — at the cost of the
 * monitoring re-draining the body on errors.
 */

let cached;

function makeIframeFetch() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return typeof fetch === 'function' ? fetch : undefined;
  }
  try {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    (document.body || document.documentElement).appendChild(iframe);
    const iframeFetch = iframe.contentWindow && iframe.contentWindow.fetch;
    iframe.remove();
    if (typeof iframeFetch === 'function') {
      return iframeFetch.bind(window);
    }
  } catch (_e) {
    /* fall through */
  }
  return typeof window !== 'undefined' && window.fetch
    ? window.fetch.bind(window)
    : undefined;
}

export function safeFetch(input, init) {
  if (!cached) cached = makeIframeFetch();
  const fn = cached || window.fetch.bind(window);
  return fn(input, init);
}

export default safeFetch;
